import { estimateTimeline, formatRange, type Script } from '@reelmachine/core';
import type { ScriptEditor } from './useScriptEditor';
import { lineName } from './ScriptTab';

const PERSON = /groß im Bild|Nahaufnahme|vor einem|close-up|on camera|talking head/i;

interface Props {
  script: Script;
  editor: ScriptEditor;
  brand: string;
  highlight: ReadonlySet<string>;
  readOnly: boolean;
  /** Viewable URL per image key (ReelDetail.imageUrls). */
  imageUrls?: Record<string, string>;
  /** Collects images for this script and places them (text stays). */
  onAddImages?: () => void;
  adding?: boolean;
  imagesError?: string;
}

const KIND_LABEL: Record<string, string> = {
  source: 'Quelle',
  person: 'Porträt',
  stock: 'Stimmung',
  screenshot: 'Beleg',
  upload: 'Upload',
};

/** The image an effect of the line shows (ken-burns, proof-screenshot), if any. */
function lineImage(line: Script['lines'][number]): string | undefined {
  for (const fx of line.fx ?? []) {
    const f = fx as Record<string, unknown>;
    if ((f.id === 'ken-burns' || f.id === 'proof-screenshot') && typeof f.src === 'string') return f.src;
  }
  return undefined;
}

/** 9:16 frames in the channel's brand colour with the on-screen text; the visual is editable per frame. */
export function StoryboardTab({
  script,
  editor,
  brand,
  highlight,
  readOnly,
  imageUrls = {},
  onAddImages,
  adding,
  imagesError,
}: Props) {
  const timeline = estimateTimeline({ lines: script.lines.map((l) => ({ ...l, text: editor.value(l.id, 'text') })) });
  const images = (script.images ?? []).filter((i) => imageUrls[i.key]);
  return (
    <>
      {imagesError && (
        <div className="card warnc" role="alert">
          <p>{imagesError}</p>
        </div>
      )}
      {images.length === 0 && onAddImages && (
        <div className="image-strip">
          <p className="muted">
            No images yet. Images from the source, portraits, Unsplash photos and proof screenshots, placed into this
            script. The text stays.
          </p>
          <button type="button" className="btn sec" disabled={adding} onClick={onAddImages}>
            {adding ? 'Starting…' : 'Add images'}
          </button>
        </div>
      )}
      {images.length > 0 && (
        <section className="image-strip" aria-label="Bilder für dieses Reel">
          <h3>Bilder ({images.length})</h3>
          <ul>
            {images.map((i) => (
              <li key={i.key}>
                <a href={i.sourceUrl ?? imageUrls[i.key]} target="_blank" rel="noreferrer" title={i.alt ?? i.name}>
                  <img src={imageUrls[i.key]} alt={i.alt ?? i.name} loading="lazy" />
                </a>
                <span className="kind">{KIND_LABEL[i.kind ?? 'upload'] ?? i.kind}</span>
                {i.credit && <span className="credit">{i.credit}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="frames">
        {script.lines.map((line, i) => {
          const visual = editor.value(line.id, 'visual');
          const src = lineImage(line);
          const img = src ? imageUrls[src] : undefined;
          return (
            <div className={`frame ${highlight.has(`line:${line.id}`) ? 'changed' : ''}`.trim()} key={line.id}>
              <div className="vf" role="img" aria-label={`${lineName(i)} frame: ${editor.value(line.id, 'onScreen')}`}>
                {img && <img className="still" src={img} alt="" loading="lazy" />}
                <div className="band" style={{ background: `linear-gradient(${brand}, transparent)` }} />
                {!img && PERSON.test(visual) && <div className="person" />}
                <span className="tc">{timeline[i] && formatRange(timeline[i])}</span>
                <div className="ost">{editor.value(line.id, 'onScreen')}</div>
              </div>
              <label className="field-l" htmlFor={`visual-${line.id}`}>
                Visual
              </label>
              <textarea
                id={`visual-${line.id}`}
                rows={3}
                maxLength={400}
                readOnly={readOnly}
                className={highlight.has(`visual:${line.id}`) ? 'changed' : undefined}
                value={visual}
                onChange={(e) => editor.set(line.id, 'visual', e.target.value)}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}
